import { computed, ref } from 'vue'

const THEME_KEY = 'budget.theme'

const themeVersion = ref(0)
let systemThemeQuery
let systemThemeListenerAttached = false

function notifyThemeChange() {
  themeVersion.value++
}

function getStoredTheme() {
  const raw = localStorage.getItem(THEME_KEY)
  return raw === 'light' || raw === 'dark' ? raw : null
}

function getSystemTheme() {
  if (typeof window.matchMedia !== 'function') {
    return 'light'
  }

  systemThemeQuery ??= window.matchMedia('(prefers-color-scheme: dark)')
  return systemThemeQuery.matches ? 'dark' : 'light'
}

export function getTheme() {
  return getStoredTheme() ?? getSystemTheme()
}

function applyTheme(theme) {
  if (theme) {
    document.documentElement.setAttribute('data-theme', theme)
  } else {
    document.documentElement.removeAttribute('data-theme')
  }

  const colorScheme = document.querySelector('meta[name="color-scheme"]')
  if (colorScheme) {
    colorScheme.content = theme ?? 'light dark'
  }
}

function handleSystemThemeChange() {
  if (getStoredTheme()) {
    return
  }

  notifyThemeChange()
}

export function initTheme() {
  applyTheme(getStoredTheme())

  if (typeof window.matchMedia === 'function' && !systemThemeListenerAttached) {
    systemThemeQuery ??= window.matchMedia('(prefers-color-scheme: dark)')
    systemThemeQuery.addEventListener('change', handleSystemThemeChange)
    systemThemeListenerAttached = true
  }
}

export function setTheme(theme) {
  if (theme !== 'light' && theme !== 'dark') {
    return
  }

  localStorage.setItem(THEME_KEY, theme)
  applyTheme(theme)
  notifyThemeChange()
}

export function setAutomaticTheme() {
  localStorage.removeItem(THEME_KEY)
  applyTheme(null)
  notifyThemeChange()
}

export function useTheme() {
  return {
    theme: computed(() => (themeVersion.value, getTheme())),
    automatic: computed(() => (themeVersion.value, getStoredTheme() === null)),
  }
}
